import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../api/client";

export function LocalLogin() {
  const qc = useQueryClient();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const login = useMutation({
    mutationFn: () => api.localLogin(username.trim(), password),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) {
        if (e.status === 401) {
          setErr("Invalid username or password.");
        } else if (e.status === 429) {
          const body = e.body as { retryAfter?: number } | null;
          const seconds = body?.retryAfter ?? 0;
          setErr(`Too many attempts. Try again in ${Math.ceil(seconds / 60)} minute(s).`);
        } else {
          setErr("Sign-in failed. Try again.");
        }
      } else {
        setErr("Sign-in failed. Try again.");
      }
    },
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950 px-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!username.trim() || !password) return;
          login.mutate();
        }}
        className="w-full max-w-sm rounded-lg border border-zinc-800 bg-zinc-900 p-6 shadow-xl"
      >
        <h1 className="mb-4 text-lg font-semibold text-zinc-100">Sign in</h1>
        <label className="mb-3 block text-xs text-zinc-400">
          Username
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            autoComplete="username"
            className="mt-1 block w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-zinc-100"
          />
        </label>
        <label className="mb-4 block text-xs text-zinc-400">
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="mt-1 block w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-zinc-100"
          />
        </label>
        {err && (
          <div className="mb-3 rounded bg-rose-900/40 px-2 py-1 text-xs text-rose-200">{err}</div>
        )}
        <button
          type="submit"
          disabled={login.isPending}
          className="w-full rounded bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
        >
          {login.isPending ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
